import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui';
import { MODAL_Z } from '@/styles/modalTokens';
import { typeRole } from '@/styles/typography';
import { useTourGuide } from './TourContext';
import { computeTooltipPlacement, getTargetRect, queryTourTarget, scrollTargetIntoView, type RectLike } from './tourUtils';
import { KIOSK_TOUR_ACTIVATE_EVENT } from './types';

/** Swallow interaction so the page under the tour cannot be clicked. */
function swallowPointer(e: React.SyntheticEvent) {
  e.preventDefault();
  e.stopPropagation();
}

function formatStepCounter(stepIndex: number, total: number): string {
  const x = Math.min(stepIndex + 1, Math.max(total, 1));
  return `${x} / ${Math.max(total, 1)}`;
}

/** v1 tour tooltip look + current shell radius. */
const TOUR_TOOLTIP_CLASS =
  'fixed border border-emerald-400/40 bg-gradient-to-br from-slate-900 via-purple-900/90 to-slate-900 backdrop-blur-md shadow-[0_0_0_1px_rgba(16,185,129,0.25),0_0_30px_rgba(16,185,129,0.25),0_25px_50px_rgba(0,0,0,0.55)]';

export function TourOverlay(props: {
  onRequestClose: () => void;
  onRequestFinish: () => void;
}) {
  const { onRequestClose, onRequestFinish } = props;
  const { status, steps, stepIndex, next, back, finish } = useTourGuide();

  const step = steps[stepIndex];
  const isRunning = status === 'running';
  const total = steps.length;

  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const [targetRect, setTargetRect] = useState<RectLike | null>(null);
  const missingTargetRetriesRef = useRef(0);

  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);

  const paddedRect = useMemo(() => {
    if (!targetRect) return null;
    const pad = 8;
    return {
      top: Math.max(targetRect.top - pad, 0),
      left: Math.max(targetRect.left - pad, 0),
      width: targetRect.width + pad * 2,
      height: targetRect.height + pad * 2,
      right: targetRect.right + pad,
      bottom: targetRect.bottom + pad,
    } satisfies RectLike;
  }, [targetRect]);

  // Resolve target element on step change
  useEffect(() => {
    if (!isRunning || !step) return;
    missingTargetRetriesRef.current = 0;
    let cancelled = false;
    let retryTimer = 0;

    // Let multi-step pages mount the matching panel before we query the DOM.
    if (step.activate) {
      window.dispatchEvent(
        new CustomEvent(KIOSK_TOUR_ACTIVATE_EVENT, { detail: step.activate })
      );
    }

    const tryResolve = () => {
      if (cancelled) return;
      const el = queryTourTarget(step.targetTourId);
      setTargetEl(el);
      if (el) {
        scrollTargetIntoView(el);
        return;
      }

      // Retry a few times to handle async rendering/data load.
      missingTargetRetriesRef.current += 1;
      if (missingTargetRetriesRef.current <= 16) {
        retryTimer = window.setTimeout(tryResolve, 250);
        return;
      }

      // Skip this step if target is still missing.
      if (stepIndex < steps.length - 1) next();
      else {
        finish();
        onRequestFinish();
      }
    };

    // Small delay so React can commit wizard step changes from activate.
    retryTimer = window.setTimeout(tryResolve, step.activate ? 80 : 0);
    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [isRunning, step, stepIndex, steps.length, next, finish, onRequestFinish]);

  // Track rect on scroll/resize while running
  useEffect(() => {
    if (!isRunning || !targetEl) return;
    let raf = 0;

    const update = () => {
      raf = window.requestAnimationFrame(() => {
        setTargetRect(getTargetRect(targetEl));
      });
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [isRunning, targetEl]);

  // Compute tooltip position after tooltip renders / rect updates
  useLayoutEffect(() => {
    if (!isRunning || !step || !paddedRect || !tooltipRef.current) {
      setTooltipPos(null);
      return;
    }
    const box = tooltipRef.current.getBoundingClientRect();
    const res = computeTooltipPlacement({
      targetRect: paddedRect,
      tooltipSize: { width: box.width, height: box.height },
      preferred: step.placement ?? 'auto',
      viewportPadding: 12,
      gap: 12,
    });
    setTooltipPos({ top: res.top, left: res.left });
  }, [isRunning, step, paddedRect]);

  useEffect(() => {
    if (!isRunning) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onRequestClose();
    };
    window.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isRunning, onRequestClose]);

  if (!isRunning || !step) return null;

  const canBack = stepIndex > 0;
  const isLast = stepIndex === total - 1;

  const handleNext = () => {
    if (isLast) {
      finish();
      onRequestFinish();
    } else {
      next();
    }
  };

  const handleClose = () => {
    onRequestClose();
  };

  return createPortal(
    <div
      className="fixed inset-0 overflow-hidden overscroll-none"
      style={{ zIndex: MODAL_Z.tour }}
      aria-modal="true"
      role="dialog"
    >
      {/* Full-screen shield — previously pointer-events-none let clicks hit the page */}
      <div
        className="absolute inset-0 z-0"
        aria-hidden
        onPointerDown={swallowPointer}
        onClick={swallowPointer}
        onWheel={swallowPointer}
        onTouchMove={swallowPointer}
      />

      {/* Cut-out overlay: darken everything except target rect (visual only) */}
      {paddedRect ? (
        <div
          className="absolute z-0 pointer-events-none"
          style={{
            top: `${paddedRect.top}px`,
            left: `${paddedRect.left}px`,
            width: `${paddedRect.width}px`,
            height: `${paddedRect.height}px`,
            borderRadius: 'var(--wf-mdl-radius, 14px)',
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.85)',
          }}
        />
      ) : (
        <div className="absolute inset-0 z-0 bg-black/85 pointer-events-none" />
      )}

      {/* Highlight box */}
      {paddedRect ? (
        <div
          className="absolute z-0 pointer-events-none"
          style={{
            top: `${paddedRect.top}px`,
            left: `${paddedRect.left}px`,
            width: `${paddedRect.width}px`,
            height: `${paddedRect.height}px`,
            borderRadius: 'var(--wf-mdl-radius, 14px)',
            border: '2px solid rgba(52, 211, 153, 0.9)',
            boxShadow:
              '0 0 0 1px rgba(16, 185, 129, 0.25), 0 0 30px rgba(16, 185, 129, 0.35), 0 0 18px rgba(16, 185, 129, 0.2) inset',
          }}
        />
      ) : null}

      {/* Tooltip — only interactive surface while the tour runs */}
      <div
        ref={tooltipRef}
        className={`${TOUR_TOOLTIP_CLASS} pointer-events-auto z-10`}
        style={{
          width: 'min(360px, calc(100vw - 24px))',
          borderRadius: 'var(--wf-mdl-radius, 16px)',
          top: tooltipPos ? `${tooltipPos.top}px` : '50%',
          left: tooltipPos ? `${tooltipPos.left}px` : '50%',
          transform: tooltipPos ? 'translate3d(0,0,0)' : 'translate(-50%,-50%)',
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="px-4 pt-4 pb-3">
          <div className={`text-white font-extrabold ${typeRole.header2}`}>{step.title}</div>
          <div className={`mt-1.5 text-white/80 ${typeRole.caption} leading-5`}>{step.description}</div>
        </div>
        <div className="flex items-center gap-2 px-4 pb-4">
          <Button type="button" variant="destructive" size="sm" onClick={handleClose}>
            Skip
          </Button>
          <div className={`flex-1 text-center text-white/60 ${typeRole.caption} font-semibold tabular-nums`}>
            {formatStepCounter(stepIndex, total)}
          </div>
          {canBack ? (
            <Button type="button" variant="tertiary" size="sm" onClick={back}>
              Back
            </Button>
          ) : null}
          <Button type="button" variant="moneyIn" size="sm" onClick={handleNext}>
            {isLast ? 'Finish' : 'Next'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
